import React, { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "antd";
import "../EditableCellGrid.css";

function normalizeEditorValue(column, value, row) {
    if (column.normalize) return column.normalize(value, row);
    return value;
}

function getEditorPlaceholder(column) {
    return column.editorOptions?.placeholder ?? column.placeholder;
}

function shouldSelectOnFocus(column) {
    return column.editorOptions?.selectOnFocus !== false;
}

function hasInitialValue(column) {
    return column.editorOptions?.hasInitialValue === true;
}

function getInputElement(inputRef) {
    return inputRef.current?.input ?? inputRef.current;
}

function focusInitialValue(inputRef, selectOnFocus) {
    const inputElement = getInputElement(inputRef);
    inputElement?.focus?.();
    if (selectOnFocus) {
        inputElement?.select?.();
        return;
    }

    const valueLength = String(inputElement?.value ?? "").length;
    inputElement?.setSelectionRange?.(valueLength, valueLength);
}

const TextCellEditor = ({ row, column, onRowChange, onClose }) => {
    const initialValue = row[column.key] ?? "";
    const [value, setValue] = useState(String(initialValue));
    const closedRef = useRef(false);
    const isComposingRef = useRef(false);
    const initialDraftSyncedRef = useRef(false);
    const inputRef = useRef(null);
    const selectOnFocus = shouldSelectOnFocus(column);

    useEffect(() => {
        focusInitialValue(inputRef, selectOnFocus);
    }, [selectOnFocus]);

    const syncRowValue = useCallback(
        (nextValue) => {
            const nextRowValue = normalizeEditorValue(column, nextValue, row);
            onRowChange({ ...row, [column.key]: nextRowValue });
        },
        [column, onRowChange, row],
    );

    useEffect(() => {
        if (!hasInitialValue(column) || initialDraftSyncedRef.current) return;
        initialDraftSyncedRef.current = true;
        const nextRowValue = normalizeEditorValue(column, row[column.key] ?? "", row);
        onRowChange({ ...row, [column.key]: nextRowValue }, false);
    }, [column, onRowChange, row]);

    const commit = useCallback((shouldFocusCell = false) => {
        if (closedRef.current) return;
        closedRef.current = true;
        onClose(true, shouldFocusCell);
    }, [onClose]);

    const handleChange = useCallback(
        (event) => {
            const nextValue = event.target.value;
            setValue(nextValue);
            if (!isComposingRef.current && !event.nativeEvent?.isComposing) {
                syncRowValue(nextValue);
            }
        },
        [syncRowValue],
    );

    const handleCompositionStart = useCallback(() => {
        isComposingRef.current = true;
    }, []);

    const handleCompositionEnd = useCallback(
        (event) => {
            isComposingRef.current = false;
            const nextValue = event.currentTarget.value;
            setValue(nextValue);
            syncRowValue(nextValue);
        },
        [syncRowValue],
    );

    return (
        <Input
            ref={inputRef}
            autoFocus
            className="editable-cell-editor editable-cell-editor-text ant-input"
            value={value}
            placeholder={getEditorPlaceholder(column)}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            onBlur={() => commit(false)}
            onKeyDown={(event) => {
                if (isComposingRef.current || event.nativeEvent?.isComposing) {
                    if (event.key === "Enter" || event.key === "Tab") event.stopPropagation();
                    return;
                }
            }}
        />
    );
};

export default TextCellEditor;
